import { useCallback, useContext, useEffect, useRef, useState } from "preact/hooks";
import { ComponentChildren, createContext } from "preact";

import { fetchChatDetails, fetchDownloadLink } from "@api/chat";
import { transformChatPage } from "@transformers/chat-data";
import { transformDownloadUrl } from "@transformers/download";

interface Meta {
  ready: boolean;
  title: string | null;
  total: number;
  offset: number;
  limit: number;
  active: string[][];
  nextPage: () => void;
  prevPage: () => void;

  download: (
    startFn: () => void,
    endFn: () => void,
  ) => Promise<void>;
}

interface ProviderProps {
  chatUuid: string;
  children: ComponentChildren;
}

interface Page {
  values: string[][];
}

interface Book {
  [key: number]: Page;
}

const ChatDataContext = createContext<Meta | null>(null);

export function ChatDataProvider({ chatUuid, children }: ProviderProps) {
  const [title, setTitle] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [active, setActive] = useState<string[][]>([]);
  const pages = useRef<Book>({}).current;
  const limit = 25;

  const loadPage = useCallback(async (pageOffset: number) => {
    if (pages[pageOffset]) {
      setActive(pages[pageOffset].values);
      return;
    }

    const data = transformChatPage(
      await fetchChatDetails(chatUuid, pageOffset, limit)
    );

    for (let i = limit; i < 5 * limit; i += limit) {
      fetchChatDetails(chatUuid, pageOffset + i, limit);
    }

    setTitle(data.name);
    setTotal(data.total);
    setActive(data.values);
    pages[pageOffset] = data;
  }, []);

  const download = useCallback(async (
    startFn: () => void,
    endFn: () => void,
  ) => {
    startFn();

    const { url } = transformDownloadUrl(
      await fetchDownloadLink(chatUuid)
    );

    if (url) {
      const link = document.createElement("a");
      link.href = `${(window as any).apiBaseUrl}${url}`;
      link.download = `${title || "report"}.csv`;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    endFn();
  }, [chatUuid, title]);

  const load = useCallback(async () => {
    setReady(false);
    await loadPage(offset);
    setReady(true);
  }, [offset]);

  useEffect(() => {
    load();
  }, [offset]);

  const value = {
    ready,
    title,
    total,
    active,
    offset,
    limit,
    download,
    nextPage: () => setOffset((prev) => prev + limit),
    prevPage: () => setOffset((prev) => Math.max(0, prev - limit)),
  };

  return <ChatDataContext.Provider value={value}>
    {children}
  </ChatDataContext.Provider>
}

export function useChatData(): Meta {
  return useContext(ChatDataContext)!;
}

